import { useCallback, useMemo } from 'react';
import { useModuleName } from 'hooks';
import { MODULE_NAME as CAMPAIGNS_MODULE_NAME } from 'modules/campaigns/config';
import { MODULE_NAME as ONBOARDING_MODULE_NAME } from 'modules/onboarding';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { BUDGET_TYPES } from '../../../campaigns/modules/common-campaign-form/config';
import { CHAT_DIALOG_MESSAGE_PROP_PATH } from '../config/chatDialogMessagePropPath';
import { getChatModalComponent } from '../transformers/getChatModalComponent';
import { useChatAgentUtils } from './useChatAgentUtils';

export const useCampaignsTools = ({ closeChat, forwardResponseToChatAgent } = {}) => {
    const moduleName = useModuleName();
    const account = useAccount();
    const { formatMessage, formatNumber } = useIntl();
    const { setFormData } = useChatAgentUtils();

    const setBudgetValue = useCallback(
        ({ budgetDesktop, budgetMobile }) => {
            if (moduleName === ONBOARDING_MODULE_NAME) {
                setFormData({
                    // TODO: define the value according to the entity structure
                    budget: { type: BUDGET_TYPES.DAILY, value: budgetDesktop || budgetMobile },
                });
                closeChat();
                return true;
            }

            if (moduleName === CAMPAIGNS_MODULE_NAME) {
                setFormData({
                    budget: { type: BUDGET_TYPES.DAILY, value: budgetDesktop || budgetMobile },
                });
                closeChat();
                return true;
            }

            return false;
        },
        [closeChat, moduleName, setFormData]
    );

    const setBudgetComponent = useCallback(
        ({ budgetDesktop, budgetMobile, ...rest }, originalMessage) => {
            const message = getChatModalComponent({
                header: formatMessage({
                    id: 'abby.budget.modal.header',
                    defaultMessage: "Abby's suggestion for daily budget",
                }),
                buttonText: formatMessage({ id: 'abby.budget.modal.button', defaultMessage: 'Apply to form' }),
                body: formatNumber(budgetDesktop || budgetMobile, {
                    style: 'currency',
                    currency: account.currency,
                }),
                methodName: 'campaigns.setBudgetValue',
                args: { budgetDesktop, budgetMobile, ...rest },
            });
            forwardResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: message, actions: [] });
        },
        [account.currency, formatMessage, formatNumber, forwardResponseToChatAgent]
    );

    return useMemo(
        () => ({
            setBudgetComponent,
            setBudgetValue,
        }),
        [setBudgetValue, setBudgetComponent]
    );
};
